import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, Input, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';

import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button-group';
import '@maersk-global/mds-components-core/mc-button-group-item';
import { RepairPartAdvanceFilterComponent } from '../repair-part-advance-filter/repair-part-advance-filter.component';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { RepairPartFilterForm } from '../../../form-model/repair-part-filter.form.model';
import { ReplaySubject } from 'rxjs';
import { RepairPartSearchTextForm } from '../../../form-model/repair-part-search-text.form.model';
import { RepairPartService } from '../../../services/repair-part.service';
import { ToastrService } from '../../../services/toastr-service';
import { environment } from '../../../../environments/environment';
import { DownloadFilterCriteria, FilterOperator, toFilter } from '../../../models/download-filter.model';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';

type FilterValue = string | string[] | boolean;

@Component({
  selector: 'app-repair-part-filters',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, RepairPartAdvanceFilterComponent, TagWithOverflowComponent],
  templateUrl: './repair-part-filter.component.html',
  styleUrl: './repair-part-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairPartFilterComponent implements AfterViewInit {
  isRepairPartAddEnabled = signal(this.authPolicies.canAddRepairPart && environment.Features.EnableRepairPart);
  readonly canViewRepairPartDownload = this.authPolicies.canViewRepairPart;
  showAdvanceFilters: boolean = false;
  isDownload: boolean = false;

  private readonly maxQuantityOperatorLabels: Record<string, string> = {
    equals: 'Equals',
    above: 'Above',
    below: 'Below',
    equalorabove: 'Equal or above',
    equalorbelow: 'Equal or below',
  };

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    repairPartFilterForm: FormGroup<RepairPartFilterForm>;
    repairPartSearchTextForm: FormGroup<RepairPartSearchTextForm>;
  }>;

  savedFilter!: FormGroup<RepairPartFilterForm>;
  taggedFilters: { key: string; value: FilterValue }[] = [];
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    public authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly repairPartService: RepairPartService,
    private readonly toastrService: ToastrService,
  ) {}

  ngAfterViewInit(): void {
    this.ensureFlagControlsExist();
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private ensureFlagControlsExist(): void {
    const filterForm = this.filterFormGroup.controls.repairPartFilterForm;
    const searchForm = this.filterFormGroup.controls.repairPartSearchTextForm;
    if (!filterForm.get('applyRepairPartFilter')) {
      filterForm.addControl('applyRepairPartFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!searchForm.get('appliedRepairPartSearchTextFilter')) {
      searchForm.addControl('appliedRepairPartSearchTextFilter', this.formBuilder.control<boolean | null>(null));
    }
  }

  private setFilterFormValue() {
    const filterValue = this.repairPartService.getRepairPartFilterFormValue();
    if (filterValue) {
      this.filterFormGroup.controls.repairPartFilterForm.setValue(filterValue);
      this.updateSelectedFiltersSection();
    }
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.controls.repairPartFilterForm.get('applyRepairPartFilter')!.valueChanges.subscribe((applyVal) => {
      if (applyVal === true) {
        this.filterFormGroup.controls.repairPartFilterForm.get('applyRepairPartFilter')!.setValue(null, { emitEvent: false });
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.repairPartService.setRepairPartFilterFormValue(this.filterFormGroup.controls.repairPartFilterForm.getRawValue());
      }
    });
    this.filterFormGroup.controls.repairPartSearchTextForm
      .get('appliedRepairPartSearchTextFilter')!
      .valueChanges.subscribe((searchApplied) => {
        if (searchApplied) {
          this.updateSelectedFiltersSection();
        }
      });
  }

  unselectSpecificFilter(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1): void {
    const form = this.filterFormGroup.controls.repairPartFilterForm;
    switch (taggedFilter.key) {
      case 'Mode':
        this.removeArrayItemFromControl(form, 'repairModeList', itemNumber);
        break;
      case 'Repair Code':
        this.removeArrayItemFromControl(form, 'repairCodeList', itemNumber, true);
        break;
      case 'Part Number':
        this.removeArrayItemFromControl(form, 'partCodeList', itemNumber, true);
        break;
      case 'Max Part Qty':
        form.get('maxQuantityOperator')?.setValue(null);
        form.get('maxQuantityValue')?.setValue(null);
        break;
      default:
        break;
    }
    form.get('applyRepairPartFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  private removeArrayItemFromControl(form: FormGroup, controlName: string, index: number, withFallback = false): void {
    const currentValues: string[] = form.get(controlName)?.value ?? [];
    if (Array.isArray(currentValues) && index >= 0 && index < currentValues.length) {
      form.get(controlName)?.setValue(currentValues.filter((_: any, i: number) => i !== index));
    } else if (withFallback) {
      form.get(controlName)?.setValue([]);
    }
  }

  showAdvanceFiltersModal() {
    this.showAdvanceFilters = true;
    this.saveCurrentFilters();
  }

  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.controls.repairPartFilterForm.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.repairPartFilterForm.get('applyRepairPartFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  private saveCurrentFilters(): void {
    const form = this.filterFormGroup.controls.repairPartFilterForm;
    this.savedFilter = this.formBuilder.group({
      repairModeList: this.formBuilder.control<string[] | null>(form.get('repairModeList')?.value ?? null),
      repairCodeList: this.formBuilder.control<string[] | null>(form.get('repairCodeList')?.value ?? null),
      partCodeList: this.formBuilder.control<string[] | null>(form.get('partCodeList')?.value ?? null),
      maxQuantityOperator: this.formBuilder.control<string | null>(form.get('maxQuantityOperator')?.value ?? null),
      maxQuantityValue: this.formBuilder.control<string | null>(form.get('maxQuantityValue')?.value ?? null),
      applyRepairPartFilter: this.formBuilder.control<boolean | null>(form.get('applyRepairPartFilter')?.value ?? null),
    });
  }

  updateSelectedFiltersSection() {
    const form = this.filterFormGroup.controls.repairPartFilterForm;
    this.markAsTagged('Mode', form.get('repairModeList')?.value ?? null);
    this.markAsTagged('Repair Code', form.get('repairCodeList')?.value ?? null);
    this.markAsTagged('Part Number', form.get('partCodeList')?.value ?? null);

    const operator = form.get('maxQuantityOperator')?.value;
    const value = form.get('maxQuantityValue')?.value;
    if (operator && value) {
      const label = this.maxQuantityOperatorLabels[operator] || operator;
      this.markAsTagged('Max Part Qty', `${label} ${value}`);
    } else {
      this.markAsTagged('Max Part Qty', null as any);
    }
  }

  markAsTagged(key: string, newValue: FilterValue | null) {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== false && newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string) {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string) {
    return getSharedFilterValue(this.taggedFilters, key);
  }

  getTaggedFilterValue(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1): FilterValue {
    switch (taggedFilter.key) {
      case 'Mode':
      case 'Repair Code':
      case 'Part Number': {
        let list = taggedFilter.value as string[];
        return itemNumber >= 0 && itemNumber < list.length ? list[itemNumber] : list;
      }
      case 'Max Part Qty':
        return taggedFilter.value;
      default:
        return '';
    }
  }

  getTaggedMode(): string[] {
    const tag = this.taggedFilters.find((f) => f.key === 'Mode');
    return tag && Array.isArray(tag.value) ? tag.value : [];
  }

  getTaggedRepairCode(): string[] {
    const tag = this.taggedFilters.find((f) => f.key === 'Repair Code');
    return tag && Array.isArray(tag.value) ? tag.value : [];
  }

  getTaggedPartNumber(): string[] {
    const tag = this.taggedFilters.find((f) => f.key === 'Part Number');
    return tag && Array.isArray(tag.value) ? tag.value : [];
  }

  unselectModeByValue = (val: string) => {
    const tag = this.taggedFilters.find((f) => f.key === 'Mode');
    if (tag) {
      const idx = (tag.value as string[]).indexOf(val);
      if (idx >= 0) this.unselectSpecificFilter(tag, idx);
    }
  };

  unselectRepairCodeByValue = (val: string) => {
    const tag = this.taggedFilters.find((f) => f.key === 'Repair Code');
    if (tag) {
      const idx = (tag.value as string[]).indexOf(val);
      if (idx >= 0) this.unselectSpecificFilter(tag, idx);
    }
  };

  unselectPartNumberByValue = (val: string) => {
    const tag = this.taggedFilters.find((f) => f.key === 'Part Number');
    if (tag) {
      const idx = (tag.value as string[]).indexOf(val);
      if (idx >= 0) this.unselectSpecificFilter(tag, idx);
    }
  };

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  onDownload(): void {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.repairPartService.downloadToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('repair-part'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.repairPartFilterForm;

    const modes = form.get('repairModeList')?.value;
    if (modes && modes.length > 0) {
      filters.push(toFilter('repair_mode', modes));
    }

    const codes = form.get('repairCodeList')?.value;
    if (codes && codes.length > 0) {
      filters.push(toFilter('repair_code', codes));
    }

    const parts = form.get('partCodeList')?.value;
    if (parts && parts.length > 0) {
      filters.push(toFilter('part_code', parts));
    }

    const maxQtyOperator = form.get('maxQuantityOperator')?.value;
    const maxQtyValue = form.get('maxQuantityValue')?.value;
    if (maxQtyOperator && maxQtyValue) {
      const operatorMap: Record<string, FilterOperator> = {
        equals: FilterOperator.Equals,
        above: FilterOperator.GreaterThan,
        below: FilterOperator.LessThan,
        equalorabove: FilterOperator.GreaterThanOrEqual,
        equalorbelow: FilterOperator.LessThanOrEqual,
      };
      const operator = operatorMap[maxQtyOperator];
      if (operator !== undefined) {
        filters.push({ column: 'max_quantity', operator, value: String(maxQtyValue) });
      }
    }

    return filters;
  }
}
